import { Search, Calendar, User, Tag, TrendingUp, Clock, ArrowRight, Mail } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'

export default function Blog() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')

  const categories = [
    'All', 'Industry News', 'Driver Tips', 'Career Advice', 
    'Company Updates', 'Logistics Trends', 'Safety'
  ]

  const blogPosts = [
    {
      id: 1,
      title: "The Future of HGV Driving: Electric Trucks and Green Logistics",
      excerpt: "Explore how the transport industry is embracing electric vehicles and what this means for professional drivers in 2025.",
      author: "Sam Wharfe (CEO, C9 Recruitment)",
      date: "2025-06-08",
      category: "Industry News",
      readTime: "8 min read",
      image: "",
      featured: true,
      slug: "/blog/future-of-hgv-driving",
      tags: ["Electric Vehicles", "Sustainability", "Future Trends"]
    },
    {
      id: 2,
      title: "Top 10 Safety Tips Every Professional Driver Should Know",
      excerpt: "Essential safety practices that can help prevent accidents and keep you safe on the road.",
      author: "Sam Wharfe (CEO, C9 Recruitment)",
      date: "2025-06-07",
      category: "Safety",
      readTime: "6 min read",
      image: "",
      featured: false,
      slug: "/blog/safety-tips",
      tags: ["Safety", "Best Practices", "Training"]
    },
    {
      id: 3,
      title: "How to Negotiate Your Salary as an HGV Driver",
      excerpt: "Practical tips for discussing pay rates and benefits with employers in the transport sector.",
      author: "Sam Wharfe (CEO, C9 Recruitment)",
      date: "2025-06-06",
      category: "Career Advice",
      readTime: "7 min read",
      image: "",
      featured: false,
      slug: "/blog/salary-negotiation",
      tags: ["Salary", "Negotiation", "Career Growth"]
    },
    {
      id: 4,
      title: "Understanding the New Driver Hours Regulations",
      excerpt: "A comprehensive guide to the latest changes in driving time and rest period regulations.",
      author: "Sam Wharfe (CEO, C9 Recruitment)",
      date: "2025-06-05",
      category: "Industry News",
      readTime: "9 min read",
      image: "",
      featured: false,
      slug: "/blog/driver-hours-regulations",
      tags: ["Regulations", "Compliance", "Legal"]
    },
    {
      id: 5,
      title: "Building a Long-Term Career in Logistics",
      excerpt: "From entry-level positions to management roles - mapping your career progression in transport.",
      author: "Sam Wharfe (CEO, C9 Recruitment)",
      date: "2025-06-04",
      category: "Career Advice",
      readTime: "10 min read",
      image: "",
      featured: false,
      slug: "/blog/career-progression",
      tags: ["Career Development", "Leadership", "Growth"]
    },
    {
      id: 6,
      title: "C9 Recruitment Expands to Scotland",
      excerpt: "We're excited to announce our expansion into the Scottish market with new opportunities.",
      author: "Sam Wharfe (CEO, C9 Recruitment)",
      date: "2025-06-03",
      category: "Company Updates",
      readTime: "5 min read",
      image: "",
      featured: false,
      slug: "/blog/scotland-expansion",
      tags: ["Expansion", "Scotland", "Opportunities"]
    }
  ]

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const featuredPost = blogPosts.find(post => post.featured)
  const regularPosts = filteredPosts.filter(post => !post.featured)

  return (
    <div>
      <SEOHead 
        title="Transport Industry News & Insights - C9 Recruitment Blog"
        description="Stay updated with the latest transport and logistics industry trends, recruitment insights, and career advice from C9 Recruitment's expert team."
        keywords="transport news, logistics insights, recruitment blog, industry trends, career advice, transport jobs, HGV news"
        canonical="/blog"
      />
      {/* Hero Section */}
      <section className="gradient-bg text-white section-padding">
        <div className="container-full text-center">
          <h1 className="text-4xl md:text-5xl font-black mb-6">
            Industry <span className="text-c9-orange">Insights</span>
          </h1>
          <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-200 mb-8">
            Stay updated with the latest news, tips, and trends in transport 
            and logistics recruitment.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 text-c9-orange mx-auto mb-3" />
              <div className="text-2xl font-bold">Weekly</div>
              <div className="text-gray-200">Industry Updates</div>
            </div>
            <div className="text-center">
              <User className="h-12 w-12 text-c9-orange mx-auto mb-3" />
              <div className="text-2xl font-bold">Expert</div>
              <div className="text-gray-200">Career Advice</div>
            </div>
            <div className="text-center">
              <Mail className="h-12 w-12 text-c9-orange mx-auto mb-3" />
              <div className="text-2xl font-bold">Free</div>
              <div className="text-gray-200">Newsletter</div>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="bg-gray-50 py-8">
        <div className="container-full">
          <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-c9-orange"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-c9-orange text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && selectedCategory === 'All' && !searchTerm && (
        <section className="section-padding">
          <div className="container-full">
            <h2 className="text-3xl font-bold text-c9-blue mb-8 text-center">Featured Article</h2>
            <div className="card overflow-hidden max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="bg-gradient-to-br from-c9-blue to-c9-orange p-16 flex items-center justify-center">
                  <div className="text-8xl">{featuredPost.image}</div>
                </div>
                <div className="p-8 lg:p-12">
                  <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                    <span className="bg-c9-orange text-white px-3 py-1 rounded-full">
                      {featuredPost.category}
                    </span>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {new Date(featuredPost.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-c9-blue mb-4">
                    {featuredPost.title}
                  </h3>
                  <p className="text-gray-600 mb-6 text-lg">
                    {featuredPost.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <User className="h-4 w-4" />
                      {featuredPost.author}
                    </div>
                    <Link to={featuredPost.slug} className="btn-primary">
                      Read Article <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="section-padding bg-gray-50">
        <div className="container-full">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold text-c9-blue">
              {searchTerm || selectedCategory !== 'All' ? 'Search Results' : 'Latest Articles'}
            </h2>
            <div className="text-gray-600">
              {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {regularPosts.map((post) => (
              <article key={post.id} className="card overflow-hidden hover:scale-105 transition-transform">
                <div className="bg-gradient-to-br from-c9-blue to-c9-orange p-8 text-center">
                  <div className="text-4xl mb-2">{post.image}</div>
                  <span className="bg-white bg-opacity-20 text-white text-xs px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-3 text-xs text-gray-500">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {new Date(post.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {post.readTime}
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold text-c9-blue mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <User className="h-3 w-3" />
                      {post.author}
                    </div>
                    <Link to={post.slug} className="text-c9-orange hover:text-c9-blue font-medium text-sm">
                      Read More →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {regularPosts.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-c9-blue mb-2">No articles found</h3>
              <p className="text-gray-600">Try adjusting your search terms or browse different categories.</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="bg-c9-blue text-white section-padding">
        <div className="container-full text-center">
          <Mail className="h-16 w-16 text-c9-orange mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Stay Informed</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-200">
            Get the latest industry insights, career tips, and job opportunities 
            delivered straight to your inbox.
          </p>
          
          <form className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-c9-orange"
              />
              <button
                type="submit"
                className="btn-secondary px-8 py-3 whitespace-nowrap"
              >
                Subscribe
              </button>
            </div>
          </form>
          
          <p className="text-sm text-gray-300 mt-4">
            Weekly updates • No spam • Unsubscribe anytime
          </p>
        </div>
      </section>

      {/* Popular Tags */}
      <section className="section-padding">
        <div className="container-full">
          <h2 className="text-3xl font-bold text-c9-blue mb-8 text-center">Popular Topics</h2>
          
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {Array.from(new Set(blogPosts.flatMap(post => post.tags))).map((tag) => (
              <button
                key={tag}
                className="flex items-center gap-2 bg-gray-100 hover:bg-c9-orange hover:text-white text-gray-700 px-4 py-2 rounded-full transition-colors"
              >
                <Tag className="h-4 w-4" />
                {tag}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gray-50 section-padding">
        <div className="container-full text-center">
          <div className="card p-12 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-c9-blue mb-4">
              Ready to Take the Next Step?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Whether you're looking for your next opportunity or need to hire top talent, 
              we're here to help you succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/candidates" className="btn-primary">
                Find Your Next Role
              </Link>
              <Link to="/clients" className="btn-secondary">
                Hire Top Talent
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
